import { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Tooltip,
  Stack,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import axios from "axios";
import { __API_URL__ } from "../../SERVER_URL";
import AddNewModal from "../../components/Modals/AddNewTrial";
import { toast } from "react-toastify";
import useCreateExternalMediaTypeMutation from "../../hooks/useCreateExternalMediaType";
import { useQueryClient } from "@tanstack/react-query";
import { CustomDatePicker } from "../UI/CustomDatePicker";

const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500 MB

export default function UploadFilesModal({
  uploadType,
  showModal,
  setShowModal,
  files,
  setFiles,
  captureDateTime,
  setCaptureDateTime,
  acceptedMimeTypes = [],
  typeList = [],
  caseId,
  getExternalMedia,
  trialId,
  sessionsList = [],
  lockedTypeId = null,
}) {
  const queryClient = useQueryClient();

  const token = localStorage.getItem("token");
  const [fileRejections, setFileRejections] = useState([]); // {file, reason}
  const [modalTypeShow, setModalTypeShow] = useState(false);
  const [title, setTitle] = useState("");
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState(caseId || "");
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const createExternalMediaTypeMutation = useCreateExternalMediaTypeMutation();
  const fileInputRef = useRef(null);

  // If caseId changes (e.g., navigated to different session), update selectedSessionId
  useEffect(() => {
    if (caseId) {
      setSelectedSessionId(caseId);
    }
  }, [caseId]);

  // Pre-select and lock the type when opened from a configured-type card
  useEffect(() => {
    if (lockedTypeId) setSelectedTypeId(lockedTypeId);
  }, [lockedTypeId, showModal]);

  // Validate per-file when selecting
  const handleFilesSelected = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;

    const accepted = [];
    const rejected = [];

    incoming.forEach((f) => {
      // Type allowed check
      if (
        acceptedMimeTypes &&
        acceptedMimeTypes.length > 0 &&
        !acceptedMimeTypes.includes(f.type)
      ) {
        rejected.push({ file: f, reason: "Invalid file type" });
        return;
      }
      // Size check
      if (f.size > MAX_FILE_SIZE) {
        rejected.push({ file: f, reason: "File too large (max 500 MB)" });
        return;
      }
      accepted.push(f);
    });

    // Append accepted to files (preserve existing)
    if (accepted.length > 0) {
      setFiles((prev) => [...prev, ...accepted]);
    }

    if (rejected.length > 0) {
      setFileRejections((prev) => [...prev, ...rejected]);
      // show toast for rejections
      rejected.forEach((r) => toast.error(`${r.file.name}: ${r.reason}`));
    }
  };

  // Remove a file from selected files
  const handleRemove = (fileToRemove) => {
    setFiles((prev) => prev.filter((f) => f !== fileToRemove));
    setFileRejections((prev) => prev.filter((r) => r.file !== fileToRemove));
  };

  // Remove rejection entry
  const removeRejection = (rej) => {
    setFileRejections((prev) => prev.filter((r) => r !== rej));
  };

  // Add new external media type
  const addExternalTypeApi = () => {
    setModalTypeShow(false);
    if (!title || title.trim() === "") {
      toast.error("Type title cannot be empty");
      return;
    }
    createExternalMediaTypeMutation.mutate(
      {
        title,
        uploadType,
        trialId,
      },
      {
        onSuccess: (res) => {
          setTitle("");
        },
      }
    );
  };

  // Validate required fields before upload
  const validateBeforeUpload = () => {
    const errs = {};
    if (!files || files.length === 0) errs.files = "Please select at least one file";
    if (!selectedTypeId) errs.type = "Please select a media type";
    if (!captureDateTime) errs.date = "Please choose capture date & time";
    if (!selectedSessionId) errs.session = "Please select a visit";
    setValidationErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Upload logic (presigned flow - unchanged)
  const saveExternalMedia = async () => {
    if (!validateBeforeUpload()) {
      // Show toast for missing requirements
      toast.error("Please fill required fields");
      return;
    }

    setIsLoading(true);
    try {
      const fileMetadata = files.map((file) => ({
        name: file.name,
        type: file.type,
      }));

      const presignedRes = await axios.post(
        `${__API_URL__}/api/photos/get_presigned_urls/`,
        { files: fileMetadata, uploadType },
        { headers: { Authorization: `Token ${token}` } }
      );

      const presignedUrls = presignedRes.data.urls || [];

      // do uploads in parallel
      const uploads = await Promise.all(
        presignedUrls.map(async (u, idx) => {
          const res = await fetch(u.upload_url, {
            method: "PUT",
            headers: { "Content-Type": u.content_type },
            body: files[idx],
          });
          return {
            success: res.ok,
            status: res.status,
            blob_path: u.blob_path,
            file_name: u.file_name,
          };
        })
      );

      const failed = uploads.filter((x) => !x.success);
      if (failed.length) {
        console.error("Failed uploads:", failed);
        toast.error(`${failed.length} files failed to upload. Please retry.`);
        setIsLoading(false);
        return;
      }

      await axios.post(
        `${__API_URL__}/api/photos/save_external_media/`,
        {
          files: presignedUrls, // contains blob_path + content_type
          type_id: selectedTypeId,
          uploadType,
          photo_session_id: selectedSessionId,
          capture_date_time: captureDateTime,
        },
        { headers: { Authorization: `Token ${token}` } }
      );

      toast.success("Media Uploaded Successfully");
      if (typeof getExternalMedia === "function") getExternalMedia();
      queryClient.invalidateQueries({ queryKey: ["visits_data"] });
      queryClient.invalidateQueries({ queryKey: ["external-media-summary"] });
      // reset & close
      setFiles([]);
      setSelectedTypeId(lockedTypeId || "");
      if (!caseId) setSelectedSessionId("");
      setFileRejections([]);
      setShowModal(false);
    } catch (err) {
      console.error("Error uploading:", err);
      toast.error("Error uploading files");
    } finally {
      setIsLoading(false);
    }
  };

  // keep disabled logic for upload button (also depends on validation)
  const isUploadDisabled = () => {
    return (
      isLoading ||
      files.length === 0 ||
      !!fileRejections.length ||
      !selectedTypeId ||
      !captureDateTime ||
      !selectedSessionId
    );
  };

  useEffect(() => {
    // Clear field errors when user fixes them
    setValidationErrors((prev) => {
      const next = { ...prev };
      if (files && files.length > 0 && next.files) delete next.files;
      if (selectedTypeId && next.type) delete next.type;
      if (captureDateTime && next.date) delete next.date;
      if (selectedSessionId && next.session) delete next.session;
      return next;
    });
  }, [files, selectedTypeId, captureDateTime, selectedSessionId]);

  // Render file preview (image or generic icon for video)
  const renderFilePreview = (file) => {
    const isImage = file.type && file.type.startsWith("image/");
    const isVideo = file.type && file.type.startsWith("video/");
    if (isImage) {
      const url = URL.createObjectURL(file);
      return (
        <CardMedia
          component="img"
          image={url}
          alt={file.name}
          sx={{ height: 140, objectFit: "cover" }}
        />
      );
    } else if (isVideo) {
      const url = URL.createObjectURL(file);
      return (
        <Box
          sx={{
            height: 140,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "grey.900",
          }}
        >
          <video src={url} style={{ maxHeight: 140, maxWidth: "100%" }} muted />
        </Box>
      );
    }
    // fallback
    return (
      <Box
        sx={{
          height: 140,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.100",
        }}
      >
        <UploadFileIcon fontSize="large" />
      </Box>
    );
  };

  return (
    <>
      <Dialog open={Boolean(showModal)} onClose={() => setShowModal(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {uploadType === "external_photos" ? "Add External Photo" : "Add External Video"}
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Left column: file uploader + previews */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Media{" "}
                <Typography component="span" color="error">
                  *
                </Typography>
              </Typography>

              <input
                ref={fileInputRef}
                accept={acceptedMimeTypes.join(",")}
                style={{ display: "none" }}
                id="external-media-file-input"
                type="file"
                multiple
                onChange={(e) => handleFilesSelected(e.target.files)}
              />
              <label htmlFor="external-media-file-input">
                <Button variant="contained" component="span" startIcon={<CloudUploadIcon />}>
                  Choose {uploadType === "external_photos" ? "Photos" : "Videos"}
                </Button>
              </label>

              <FormHelperText sx={{ mt: 1 }}>
                You can upload multiple files. Each file up to 500 MB.
              </FormHelperText>
              <FormHelperText>
                Allowed types: {acceptedMimeTypes.join(", ") || "any"}.
              </FormHelperText>

              {validationErrors.files && (
                <FormHelperText error sx={{ mt: 1 }}>
                  {validationErrors.files}
                </FormHelperText>
              )}

              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  {files && files.length > 0 ? (
                    files.map((file, idx) => (
                      <Grid item xs={12} sm={6} md={6} key={`${file.name}-${idx}`}>
                        <Card variant="outlined">
                          {renderFilePreview(file)}
                          <CardContent>
                            <Typography variant="body2" noWrap>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(file.size / 1024 / 1024).toFixed(2)} MB • {file.type || "unknown"}
                            </Typography>
                          </CardContent>
                          <CardActions sx={{ justifyContent: "space-between" }}>
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                              {idx + 1}/{files.length}
                            </Typography>
                            <Stack direction="row" spacing={1}>
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemove(file)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        No files selected yet.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* show file rejections if any */}
              {fileRejections && fileRejections.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="error">
                    Rejected files
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  {fileRejections.map((r, i) => (
                    <Box
                      key={`rej-${i}`}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 0.5,
                      }}
                    >
                      <Typography variant="body2">
                        {r.file?.name} —{" "}
                        <Typography component="span" color="error">
                          {r.reason}
                        </Typography>
                      </Typography>
                      <Button size="small" onClick={() => removeRejection(r)}>
                        Dismiss
                      </Button>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>

            {/* Right column: Session select, Type select, date/time */}
            <Grid item xs={12} md={6}>
              {/* Session dropdown - only show when sessionsList is provided and caseId is not */}
              {sessionsList && sessionsList.length > 0 && !caseId && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Visit{" "}
                    <Typography component="span" color="error">
                      *
                    </Typography>
                  </Typography>

                  <FormControl fullWidth error={Boolean(validationErrors.session)}>
                    <InputLabel id="session-select-label">Select Visit</InputLabel>
                    <Select
                      labelId="session-select-label"
                      value={selectedSessionId || ""}
                      label="Select Visit"
                      onChange={(e) => setSelectedSessionId(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Select</em>
                      </MenuItem>
                      {sessionsList.map((session) => (
                        <MenuItem key={session.session_id} value={session.session_id}>
                          {session.visit_name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {validationErrors.session || "Choose the visit"}
                    </FormHelperText>
                  </FormControl>
                </Box>
              )}

              <Typography variant="subtitle1" gutterBottom>
                {uploadType === "external_photos" ? "External Photo" : "External Video"} Type{" "}
                <Typography component="span" color="error">
                  *
                </Typography>
              </Typography>

              <FormControl fullWidth error={Boolean(validationErrors.type)}>
                <InputLabel id="external-type-label">Select Type</InputLabel>
                <Select
                  labelId="external-type-label"
                  value={selectedTypeId || ""}
                  label="Select Type"
                  disabled={Boolean(lockedTypeId)}
                  onChange={(e) => setSelectedTypeId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  {typeList && typeList.length > 0 ? (
                    typeList.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.title}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No types available</MenuItem>
                  )}
                </Select>
                <FormHelperText>{validationErrors.type || "Choose the media type"}</FormHelperText>
              </FormControl>

              {!lockedTypeId && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    sx={{ mt: 1 }}
                    onClick={() => setModalTypeShow(true)}
                  >
                    Add New Type
                  </Button>
                </Box>
              )}

              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Capture Date &amp; Time{" "}
                  <Typography component="span" color="error">
                    *
                  </Typography>
                </Typography>

                <CustomDatePicker
                  id="capture_date_time"
                  variant="outlined"
                  size="lg"
                  labelFontSize="1rem"
                  inputFontSize="1rem"
                  value={captureDateTime}
                  onChange={(val) => setCaptureDateTime(val)}
                  error={Boolean(validationErrors.date)}
                  helperText={validationErrors.date || "Select capture date & time"}
                  mode={"datetime"}
                />
              </Box>

              <Box sx={{ mt: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  When ready, click Upload. Required fields are marked with{" "}
                  <Typography component="span" color="error">
                    *
                  </Typography>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowModal(false)} disabled={isLoading}>
            Cancel
          </Button>

          <Box sx={{ position: "relative" }}>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={saveExternalMedia}
              disabled={isUploadDisabled()}
            >
              {isLoading ? "Uploading..." : "Upload"}
            </Button>
            {isLoading && (
              <CircularProgress
                size={24}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Add new type modal (keep existing component) */}
      {modalTypeShow && (
        <AddNewModal
          show={modalTypeShow}
          onHide={() => setModalTypeShow(false)}
          formSubmissionApi={addExternalTypeApi}
          modalShow={setModalTypeShow}
          title={title}
          setTitle={setTitle}
        />
      )}
    </>
  );
}
